import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    Badge,
    Box,
    Divider,
    Flex,
    IconButton,
    Link,
    Text,
    Tooltip,
    useColorModeValue,
} from "@chakra-ui/react";
import TimelineRow from "components/Tables/TimelineRow";
import {
    MdAssignmentInd,
    MdCancel,
    MdCheckCircle,
    MdDescription,
    MdFolder,
    MdGavel,
    MdLocalPostOffice,
    MdMailOutline,
    MdPayment,
    MdPrint,
    MdWarning,
} from "react-icons/md";
import { ROUTES } from "constants/routes-path";
import { currentUserHasPermission } from "utils/permission";
import { PROCESS_STATUS } from "services/processesService";
const { PERMISSIONS } = require("constants/permission");

const iconMap = {
    "infraction-committed": MdWarning,
    "infraction-issued": MdDescription,
    "infraction-notice-emitted": MdMailOutline,
    "infraction-notice-posted": MdLocalPostOffice,
    "infraction-notice-sne": MdAssignmentInd,
    "driver-indicated": MdAssignmentInd,
    "penalty-emitted": MdGavel,
    "penalty-posted": MdLocalPostOffice,
    "penalty-sne": MdAssignmentInd,
    "payment-confirmed": MdPayment,
    "canceled": MdCancel,
    "deferred": MdCheckCircle,
    "invalidated": MdCancel,
    "process-entry": MdFolder,
};

// Parses both Brazilian "dd/mm/yyyy HH:mm" and ISO date strings for sorting
const parseDateForSort = (dateStr) => {
    if (!dateStr) return 0;
    const brMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (brMatch) {
        const [, d, m, y, h = "0", min = "0", s = "0"] = brMatch;
        return new Date(+y, +m - 1, +d, +h, +min, +s).getTime();
    }
    const ts = Date.parse(dateStr);
    return isNaN(ts) ? 0 : ts;
};

function TrafficViolationTimeline({ timelineData, processos }) {
    const { t } = useTranslation();
    const textColor = useColorModeValue("gray.700", "white");
    const borderColor = useColorModeValue("#718096", "#4A5568");
    const isAdmin = currentUserHasPermission(PERMISSIONS.ADMINISTRATOR);

    const combinedTimeline = useMemo(() => {
        const existingEntries = timelineData?.timeline ?? [];
        const processosList = processos ?? [];

        const deferredProcess = processosList.find(p => p.status === PROCESS_STATUS.ACCEPTED);

        const annotatedExisting = existingEntries.map(e => ({
            ...e,
            _type: "existing",
            tooltip: e.icon === "deferred" && deferredProcess?.decisao ? deferredProcess.decisao : undefined,
        }));

        const processEntries = processosList
            .filter(p => p.data_criacao)
            .map(p => ({
                date: p.data_criacao,
                icon: "process-entry",
                _type: "process",
                processId: p.id,
                processNumero: p.numero,
                processStatus: p.status,
                processDecisao: p.decisao,
            }));

        return [...annotatedExisting, ...processEntries]
            .sort((a, b) => parseDateForSort(a.date) - parseDateForSort(b.date));
    }, [timelineData?.timeline, processos]);

    return (
        <>
            <Flex justify="space-between" align="center">
                <Text fontSize="md" fontWeight="bold" color={textColor}>
                    {t("traffic-violation.details.tab-timeline.title")}
                </Text>
                <Tooltip label="Imprimir Espelho da Infração">
                    <IconButton
                        size="sm"
                        icon={<MdPrint />}
                        onClick={() =>
                            window.open(
                                `${window.location.origin}${ROUTES.trafficViolationsDetailsTimelinePrintPath.replace(":id", timelineData.id)}`,
                                "_blank"
                            )
                        }
                        border={`solid 0.1em ${borderColor}`}
                        aria-label="Imprimir Espelho da Infração"
                    />
                </Tooltip>
            </Flex>
            <Divider borderColor="#3884cc" mt="10px" />
            <Box m="4">
                {combinedTimeline.map((entry, index) => {
                    if (entry._type === "process") {
                        const processUrl = ROUTES.processesDetailsPath.replace(":id", entry.processId);
                        const isIndeferido = entry.processStatus === PROCESS_STATUS.REJECTED;

                        const titleNode = (
                            <Flex align="center" gap={2} wrap="wrap">
                                <Text as="span">Entrada de Processo</Text>
                                <Link href={processUrl} color="blue.500" fontWeight="bold">
                                    {entry.processNumero}
                                </Link>
                                {isIndeferido && (
                                    <Badge colorScheme="red" fontSize="xs">INDEFERIDA</Badge>
                                )}
                            </Flex>
                        );

                        return (
                            <TimelineRow
                                key={`process-${entry.processId}-${index}`}
                                title={titleNode}
                                titleColor={textColor}
                                date={entry.date}
                                color="blue.400"
                                logo={iconMap["process-entry"]}
                                index={index}
                                arrLength={combinedTimeline.length}
                                subLabel={isIndeferido && entry.processDecisao ? entry.processDecisao : undefined}
                                subLabelColor="red.500"
                            />
                        );
                    }

                    const rowProps = {
                        title: entry.description,
                        titleColor: textColor,
                        date: entry.date,
                        color: textColor,
                        logo: iconMap[entry.icon],
                        index,
                        arrLength: combinedTimeline.length,
                        subLabel: isAdmin ? entry.sub_label : null,
                        subLabelColor: entry.sub_label?.startsWith("Erro") ? "red.500" : "green.500",
                    };

                    if (entry.tooltip) {
                        return (
                            <Tooltip key={`existing-${index}`} label={entry.tooltip} hasArrow placement="top">
                                <Box><TimelineRow {...rowProps} /></Box>
                            </Tooltip>
                        );
                    }

                    return <TimelineRow key={`existing-${index}`} {...rowProps} />;
                })}
            </Box>
        </>
    );
}

export default TrafficViolationTimeline;
